import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container } from '../../components/ui/Container';
import { PageContainer } from '../../components/layout/PageContainer';
import { Button } from '../../components/ui/Button';
import { ArrowLeft, FileText } from 'lucide-react';
import { categories } from '../../components/leasing-store/filters/categories';
import { useTranslation } from 'react-i18next';

export function CustomRequest() {
	const { t } = useTranslation();
  const [formData, setFormData] = useState({
    requestType: 'leasing',
    leasingCode: '',
    equipmentType: '',
    description: '',
    budget: '',
    timeline: '',
    specifications: '',
    deliveryAddress: '',
    message: '',
  });

  const [filePreviews, setFilePreviews] = useState<{ url: string; isPdf: boolean; name: string }[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);

  const handleFileChange = (files: FileList | null) => {
    if (files) {
      Array.from(files).forEach((file) => {
        const isPdf = file.type === 'application/pdf';
        const reader = new FileReader();
        const preview = { url: '', isPdf, name: file.name };
        reader.onload = () => {
          preview.url = reader.result as string;
          setFilePreviews((prev) => [...prev, preview]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleFileDelete = (fileName: string) => {
    setFilePreviews((prev) => prev.filter((file) => file.name !== fileName));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = () => {
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
    handleFileChange(e.dataTransfer.files);
  };

  const handleBrowseFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileChange(e.target.files);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.requestType === 'leasing' && (!formData.leasingCode || !/^WL-\w{8}$/.test(formData.leasingCode))) {
		  alert(t('store.page.customRequest.validation.leasingCodeInvalid'));
      return;
    }
    console.log('Custom request submitted:', formData);
    console.log('Uploaded files:', filePreviews);
    // TODO: Implement submission logic
  };

  return (
    <PageContainer>
      <div className="bg-gray-50">
        <Container className="py-16 flex justify-center">
          <div className="w-full max-w-2xl bg-white rounded-lg shadow-md p-8">
            <Link
              to="/leasing-store"
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
			  {t('store.page.customRequest.backToStore')}
            </Link>

            <div className="mt-8">
              <h1 className="text-3xl font-bold text-gray-900">
				{t('store.page.customRequest.title')}
              </h1>
              <p className="mt-4 text-gray-600 leading-relaxed">
				{t('store.page.customRequest.subtitle')}
              </p>

              <form onSubmit={handleSubmit} className="mt-8">
                <div className="space-y-8">
                  <div>
                    <label className="block text-sm font-medium text-gray-800">
					{t('store.page.customRequest.requestType')}
                    </label>
                    <select
                      value={formData.requestType}
                      onChange={e => setFormData({ ...formData, requestType: e.target.value })}
                      className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary py-2.5"
                      required
                    >
					  <option value="leasing">{t('store.paymentType.leasing')}</option>
					  <option value="location">{t('store.paymentType.location')}</option>
					  <option value="achat">{t('store.paymentType.installment')}</option>
                    </select>
                  </div>

                  {/* Code de leasing et message Wanzo si leasing */}
                  {formData.requestType === 'leasing' && (
                    <>
                      <div className="mb-2 p-3 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 text-sm rounded">
            {t('store.page.customRequest.leasingInfo.line1Before')}
             <b>{t('store.page.customRequest.leasingInfo.line1Bold')}</b>
             {t('store.page.customRequest.leasingInfo.line1After')}
            <br />
            {t('store.page.customRequest.leasingInfo.line2Before')}
             <b>{t('store.page.customRequest.leasingInfo.line2Bold')}</b>
             {t('store.page.customRequest.leasingInfo.line2After')}
            <br />
            {t('store.page.customRequest.leasingInfo.exampleLabel')}{' '}
            <span className="font-mono">WL-XXXXXXXX</span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
						{t('store.page.customRequest.leasingCode')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.leasingCode}
                          onChange={e => setFormData({ ...formData, leasingCode: e.target.value.toUpperCase() })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary font-mono"
                          placeholder="WL-XXXXXXXX"
                          required={formData.requestType === 'leasing'}
                          maxLength={11}
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
					{t('store.page.customRequest.equipmentType')}
                    </label>
                    <select
                      value={formData.equipmentType}
                      onChange={(e) => setFormData({ ...formData, equipmentType: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                      required
                    >
					  <option value="">{t('store.page.customRequest.selectEquipmentType')}</option>
                      {Object.entries(categories).map(([catKey, cat]) => (
						<optgroup
							key={catKey}
							label={t(`store.categories.${catKey}.name`, { defaultValue: cat.name })}
						>
                          {cat.subcategories.map((sub) => (
							<option key={sub.id} value={sub.id}>
								{t(`store.subcategories.${sub.id}.name`, { defaultValue: sub.name })}
							</option>
                          ))}
                        </optgroup>
                      ))}
					  <option value="autre">{t('store.page.customRequest.other')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
					{t('store.page.customRequest.detailedDescription')}
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
					  placeholder={t('store.page.customRequest.descriptionPlaceholder')}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
						{t('store.page.customRequest.estimatedBudget')}
                      </label>
                      <input
                        type="number"
                        value={formData.budget}
                        onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
						placeholder={t('store.page.customRequest.budgetPlaceholder')}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
						{t('store.page.customRequest.timeline')}
                      </label>
                      <select
                        value={formData.timeline}
                        onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                        required
                      >
						<option value="">{t('store.page.customRequest.selectTimeline')}</option>
						<option value="urgent">{t('store.page.customRequest.timelineOptions.urgent')}</option>
						<option value="1-3">{t('store.page.customRequest.timelineOptions.1-3')}</option>
						<option value="3-6">{t('store.page.customRequest.timelineOptions.3-6')}</option>
						<option value="6+">{t('store.page.customRequest.timelineOptions.6+')}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
					{t('store.page.customRequest.technicalSpecsOptional')}
                    </label>
                    <textarea
                      value={formData.specifications}
                      onChange={(e) => setFormData({ ...formData, specifications: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
					  placeholder={t('store.page.customRequest.technicalSpecsPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
					{t('store.page.customRequest.attachmentsOptional')}
                    </label>
                    <div
                      className={`mt-1 flex flex-col items-center justify-center w-full h-32 border-2 ${
                        isDragActive ? 'border-primary' : 'border-dashed border-gray-300'
                      } rounded-md bg-gray-50`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
					  <p className="text-sm text-gray-500">{t('store.page.customRequest.dragDrop')}</p>
					  <p className="text-sm text-gray-500">{t('store.page.customRequest.or')}</p>
                      <label
                        htmlFor="file-upload"
                        className="cursor-pointer text-primary underline"
                      >
						{t('store.page.customRequest.browse')}
                      </label>
                      <input
                        id="file-upload"
                        type="file"
                        accept="image/*,application/pdf"
                        multiple
                        className="hidden"
                        onChange={handleBrowseFiles}
                      />
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-2">
                      {filePreviews.map((file, index) => (
                        <div key={index} className="relative group">
                          {file.isPdf ? (
                            <div className="flex items-center justify-center h-20 w-20 bg-gray-100 border rounded-md">
                              <FileText className="h-8 w-8 text-gray-500" />
                            </div>
                          ) : (
                            <img
                              src={file.url}
							alt={t('store.page.customRequest.previewAlt', { index: index + 1 })}
                              className="h-20 w-20 object-cover rounded-md border"
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => handleFileDelete(file.name)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
							aria-label={t('store.page.customRequest.removeFile')}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <h2 className="text-lg font-medium text-gray-900">
					  {t('store.page.customRequest.deliveryInfo')}
                    </h2>

                    <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
						{t('store.page.customRequest.deliveryAddress')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.deliveryAddress}
                          onChange={e => setFormData({ ...formData, deliveryAddress: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
						placeholder={t('store.page.customRequest.deliveryAddressPlaceholder')}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
					{t('store.page.customRequest.messageOptional')}
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={e => setFormData({ ...formData, message: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit">
					  {t('store.page.customRequest.submit')}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </Container>
      </div>
    </PageContainer>
  );
}